// src/scenes/warehouse/CreateWarehouse.jsx
import { useState, useEffect } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";
import {normalizeEthiopianMobile} from "../../util/phone"

/* ---------- helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    const def =
      f.default ??
      (f.type === "checkbox" ? false : f.type === "number" ? "" : "");
    acc[f.name] = def;
    return acc;
  }, {});

const buildSchema = (fields) => {
  const shape = {};
  fields.forEach((f) => {
    let rule;
    switch (f.type) {
      case "email":
        rule = yup.string().email("invalid email");
        break;
      case "number":
        rule = yup.number().typeError("must be a number");
        break;
      case "checkbox":
        rule = yup.boolean();
        break;
      case "date":
        rule = yup.date().typeError("invalid date");
        break;
      default:
        rule = yup.string();
    }
    if (f.required) rule = rule.required("required");
    if (f.min != null) rule = rule.min(f.min);
    if (f.max != null) rule = rule.max(f.max);
    if (f.matches) rule = rule.matches(f.matches.regex, f.matches.message);
    shape[f.name] = rule;
  });
  return yup.object().shape(shape);
};

/* ---------- API ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

async function postJSON(path, data) {
  try {
    const res = await api.post(path, data);
    return { data: res.data, error: null };
  } catch (err) {
    const serverErrors =
      err?.response?.data?.errors ||
      err?.response?.data?.message ||
      err.message;
    return { data: null, error: serverErrors };
  }
}

/* ---------- page config (fields must match your backend) ---------- */
const FIELDS = [
  
   {
    name: "name",
    label: "Customer Name",
    type: "text",
    span: 2,
    required: true,
    props: { placeholder: "eg. ABC Traders" },
  },
  {
    name: "type",
    label: "Type",
    type: "select", // 👈 dropdown for supplier or customer
    span: 2,
    required: true,
    options: [
      { label: "Supplier", value: "supplier" },
      { label: "Customer", value: "customer" },
    ],
  },
  {
   
  name: "phone",
  label: "Phone Number",
    type: "number",
    span: 2,
    required: true,
    props: { placeholder: "eg. +251911234567" },
  },
  {
    name: "tinNumber",
    label: "TIN Number(optional)",
    type: "text",
    span: 2,
    required: false,
    props: { placeholder: "eg. 0000987654" },
  },
  {
    name: "address",
    label: "Address",
    type: "text",
    span: 4,
    required: true,
    props: { placeholder: "eg. Addis Ababa, Ethiopia" },
  },
];

const INITIALS = buildInitials(FIELDS);
const SCHEMA = buildSchema(FIELDS);

/* ---------- page ---------- */
export default function CreateCustomer() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);

  const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
const loggedUserId = loggedUser?.id || null;
const roleName = loggedUser?.roleName || "";

const isSalesUser = roleName.toLowerCase().includes("sales");


const loadUsers = async () => {
  try {
    const res = await api.get("/api/user", {
      params: { t: Date.now() },
    });

    const arr = (x) => (Array.isArray(x) ? x : []);

    const salesUsers = arr(res.data?.data).filter(
      (u) => String(u?.role?.name || "").toLowerCase() === "sales"
    );

    setUsers(salesUsers);
  } catch {
    setToast({
      open: true,
      msg: "Failed to load users",
      sev: "error",
    });
  }
};


useEffect(() => {
  if (!isSalesUser) {
    loadUsers();
  }
}, []);


  const handleSubmit = async (
    values,
   
    { setSubmitting, setErrors, resetForm }
  ) => {
     const { ok, value, error } = normalizeEthiopianMobile(values.phone);
    if (!ok) {
      setToast({ open: true, msg: error, sev: "error" });
      setSubmitting(false);
      return;
    }

    values.phone = value;



const payload = {
 
  userId: isSalesUser ? loggedUserId : values.userId || null,
  name: values.name,
  tinNumber: values.tinNumber || null,
  type: values.type,
  phone: values.phone,
  address: values.address,
};

    const { data, error: apiError } = await postJSON("/api/customer", payload);
    setSubmitting(false);

    if (apiError) {
      if (typeof apiError === "object" && !Array.isArray(apiError)) setErrors(apiError);
      setToast({ open: true, msg: "Failed to create customer", sev: "error" });
      return;
    }

    const msg = data?.message || "customer created successfully";
    setToast({ open: true, msg, sev: "success" });
    resetForm();
  };


  const fields = [
  ...(!isSalesUser
    ? [
        {
          name: "userId",
          label: "User",
          type: "select",
          span: 2,
          required: true,
          options: users.map((u) => ({
            label: u.fullName || u.username,
            value: u.id,
          })),
        },
      ]
    : []),
  ...FIELDS,
];


const INITIALS = buildInitials(fields);
const SCHEMA = buildSchema(fields);


  return (
    <>
      <SimpleForm
        title="CREATE CUSTOMER"
        subtitle="Add a new customer"
        Header={Header}
        fields={fields}
        initialValues={INITIALS}
        validation={SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create customer"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
